import { Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { catchError, EMPTY, finalize, tap } from 'rxjs';
import { produce } from 'immer';
import { NavController, ToastController } from '@ionic/angular';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoService } from '@ngneat/transloco';

import {
  CLOSING_THE_DAY_STATE_TOKEN,
  ClosingTheDayStateModel as StateModel
} from './closing-the-day.model';
import {
  CheckEndOfDayTickets,
  CloseCtdTicket,
  CloseCtdTicketSuccess,
  CtdCloseAllPaidTickets,
  CtdSelectBtrDiner,
  CtdSelectChargeAccount,
  CtdSelectChargeAccountDepartment,
  CtdSelectPaymentMethod,
  CtdUpdateTicket,
  GetEndOfDayTickets,
  VoidAllUnpaidTickets
} from './closing-the-day.action';
import { PosManagerService, TicketService } from 'src/app/services';
import { LOCATION_STATE_TOKEN } from 'src/app/store/location/location.state';
import { TicketClosedFromSourceMapper, TicketStatus } from 'src/app/models';
import { ExceptionError } from 'src/app/store/error/error.action';
import { APP_STATE_TOKEN } from 'src/app/store/app/app.state.model';

@State({
  name: CLOSING_THE_DAY_STATE_TOKEN,
  defaults: {
    endOfDaysCounts: {
      isLoaded: false,
      isProcessing: false,
      data: null
    },
    tickets: {
      isLoaded: false,
      isProcessing: false,
      data: null
    },
    selectedTicketAndData: {
      data: {},
      isLoaded: false,
      isProcessing: false
    }
  }
})
@Injectable()
export class ClosingTheDayState {
  constructor(
    private readonly store: Store,
    private readonly posManagerService: PosManagerService,
    private readonly navCtrl: NavController,
    private readonly toastCtrl: ToastController,
    private readonly ticketService: TicketService,
    private readonly translocoService: TranslocoService
  ) {}

  @Action(CheckEndOfDayTickets)
  checkEndOfDayTickets(
    { setState }: StateContext<StateModel>,
    { date }: CheckEndOfDayTickets
  ) {
    const pos_location_id =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.id;

    setState(
      produce((draft) => {
        draft.endOfDaysCounts.data = {
          ...draft.endOfDaysCounts.data,
          date
        };
        draft.endOfDaysCounts.isLoaded = false;
        draft.endOfDaysCounts.isProcessing = true;
      })
    );

    return this.posManagerService
      .getEndOfDaysCounts({ date, pos_location_id })
      .pipe(
        tap((res) =>
          setState(
            produce((draft) => {
              draft.endOfDaysCounts.data = res;
              draft.endOfDaysCounts.isLoaded = true;
              draft.endOfDaysCounts.isProcessing = false;
            })
          )
        ),
        catchError(() => {
          setState(
            produce((draft) => {
              draft.endOfDaysCounts.isLoaded = false;
              draft.endOfDaysCounts.isProcessing = false;
            })
          );
          return EMPTY;
        })
      );
  }

  @Action(GetEndOfDayTickets)
  getEndOfDayTickets(
    { setState, getState }: StateContext<StateModel>,
    { status }: GetEndOfDayTickets
  ) {
    const pos_location_id =
      this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.id;
    const date = getState().endOfDaysCounts.data.date;

    setState(
      produce((draft) => {
        draft.tickets.isProcessing = true;
        draft.tickets.isLoaded = false;
        draft.tickets.data = null;
      })
    );

    return this.posManagerService
      .getEndOfDayPosTickets({
        status,
        pos_location_id,
        date
      })
      .pipe(
        tap((res) =>
          setState(
            produce((draft) => {
              draft.tickets.data = res;
              draft.tickets.isLoaded = true;
            })
          )
        ),
        finalize(() =>
          setState(
            produce((draft) => {
              draft.tickets.isProcessing = false;
            })
          )
        )
      );
  }

  @Action(VoidAllUnpaidTickets)
  voidAllUnpaidTickets(
    { getState, setState, dispatch }: StateContext<StateModel>,
    { pos_operator_id }: VoidAllUnpaidTickets
  ) {
    const currentOperatorId =
      this.store.selectSnapshot(APP_STATE_TOKEN).operator.id;
    return this.posManagerService
      .voidAllUnpaidEndOfDayTickets({
        date: getState().endOfDaysCounts.data.date,
        pos_location_id:
          this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.id,
        void_operator_id: currentOperatorId,
        approving_void_operator_id: pos_operator_id
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          dispatch(
            new ExceptionError(error.error?.message || error.status + ' Error')
          );
          return EMPTY;
        }),
        tap(() => {
          setState(
            produce((draft) => {
              draft.endOfDaysCounts.data.unpaid_count = 0;
              draft.tickets.data.tickets = [];
              draft.tickets.data.total = 0;
              draft.tickets.data.count = 0;
            })
          );
          this.navCtrl.back();
          this.toastCtrl
            .create({
              color: 'success',
              message: this.translocoService.translate(
                'closingTheDay.unpaidOrdersVoidedSuccessfully'
              ),
              position: 'bottom',
              duration: 3000
            })
            .then((toast) => {
              toast.present();
            });
        })
      );
  }

  @Action(CloseCtdTicket)
  closeCtdTicket(
    { getState, dispatch }: StateContext<StateModel>,
    { device_ticket_uuid }: CloseCtdTicket
  ) {
    return this.ticketService
      .closeTicket({
        device_ticket_uuid,
        closed_from_source:
          TicketClosedFromSourceMapper[getState().tickets.data.status]
      })
      .pipe(
        tap((ticketStatus) =>
          dispatch(new CloseCtdTicketSuccess(device_ticket_uuid, ticketStatus))
        )
      );
  }

  @Action(CloseCtdTicketSuccess)
  closeCtdTicketSuccess(
    { setState }: StateContext<StateModel>,
    { device_ticket_uuid, ticketStatus }: CloseCtdTicketSuccess
  ) {
    setState(
      produce((draft) => {
        const writableTicket = draft.tickets.data.tickets.find(
          (x) => x.device_ticket_uuid === device_ticket_uuid
        );

        if (writableTicket) {
          writableTicket.status = ticketStatus;
        }
      })
    );
  }

  @Action(CtdSelectPaymentMethod)
  ctdSelectPaymentMethod(
    { setState }: StateContext<StateModel>,
    { selectedMethodId }: CtdSelectPaymentMethod
  ) {
    setState(
      produce((draft) => {
        draft.selectedTicketAndData.data.selectedPaymentMethod =
          selectedMethodId;

        if (!selectedMethodId) {
          draft.selectedTicketAndData.data.btrDiner = undefined;
          draft.selectedTicketAndData.data.selectedDinerChargeAccountId =
            undefined;
          draft.selectedTicketAndData.data.selectedDepartmentAccount =
            undefined;
        }
      })
    );
  }

  @Action(CtdSelectBtrDiner)
  ctdSelectBtrDiner(
    { setState }: StateContext<StateModel>,
    { diner }: CtdSelectBtrDiner
  ) {
    setState(
      produce((draft) => {
        draft.selectedTicketAndData.data.btrDiner = diner;
      })
    );
  }

  @Action(CtdSelectChargeAccount)
  ctdSelectChargeAccount(
    { setState }: StateContext<StateModel>,
    { id }: CtdSelectChargeAccount
  ) {
    setState(
      produce((draft) => {
        draft.selectedTicketAndData.data.selectedDinerChargeAccountId = id;
      })
    );
  }

  @Action(CtdSelectChargeAccountDepartment)
  ctdSelectChargeAccountDepartment(
    { setState }: StateContext<StateModel>,
    { selected }: CtdSelectChargeAccountDepartment
  ) {
    setState(
      produce((draft) => {
        draft.selectedTicketAndData.data.selectedDepartmentAccount = selected;
      })
    );
  }

  @Action(CtdCloseAllPaidTickets)
  ctdCloseAllPaidTickets(
    { setState }: StateContext<StateModel>,
    { date }: CtdCloseAllPaidTickets
  ) {
    setState(
      produce((draft) => {
        draft.tickets.isProcessing = true;
      })
    );

    return this.posManagerService
      .closeAllPaidTickets({
        date,
        pos_location_id:
          this.store.selectSnapshot(LOCATION_STATE_TOKEN).location.id
      })
      .pipe(
        tap(() => {
          setState(
            produce((draft) => {
              draft.tickets.data.total = 0;
              draft.tickets.data.tickets.forEach((draftTicket) => {
                draftTicket.status = TicketStatus.CLOSED;
              });
            })
          );
        }),
        finalize(() =>
          setState(
            produce((draft) => {
              draft.tickets.isProcessing = false;
            })
          )
        )
      );
  }

  @Action(CtdUpdateTicket)
  ctdUpdateTicket(
    { setState }: StateContext<StateModel>,
    { device_ticket_uuid, data }: CtdUpdateTicket
  ) {
    setState(
      produce((draft) => {
        const ticket = draft.tickets.data.tickets.find(
          (t) => t.device_ticket_uuid === device_ticket_uuid
        );

        if (ticket) {
          Object.assign(ticket, data);
        }
      })
    );
  }
}
